/**
 * ClientNomineesView Component
 * Shows limited Family Vault data (client name + nominees) for Advisors/Managers
 */
import React, { useState, useEffect } from 'react';
import { FaUsers, FaSpinner, FaUser, FaPhone, FaEnvelope, FaChevronDown, FaChevronUp, FaShieldAlt } from 'react-icons/fa';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ClientNomineesView = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedClient, setExpandedClient] = useState(null);

  const getToken = () => localStorage.getItem('token');
  const headers = () => ({ Authorization: `Bearer ${getToken()}` });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const response = await axios.get(`${API}/advisor/clients-family-vault`, { headers: headers() });
      setData(response.data || []);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="bg-[#0F172A] rounded-xl p-6 border border-[#2D5A8F]">
        <div className="flex items-center justify-center py-8">
          <FaSpinner className="animate-spin text-[#FFD700] text-2xl" />
        </div>
      </div>
    );
  }

  if (data.length === 0) {
    return null; // Don't render if no data
  }

  return (
    <div className="bg-[#0F172A] rounded-xl p-6 border border-[#2D5A8F]">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-purple-400 to-purple-600 flex items-center justify-center">
            <FaShieldAlt className="text-white" />
          </div>
          <div>
            <h3 className="text-white font-bold text-lg">My Family Should Know - Client Nominees</h3>
            <p className="text-gray-400 text-sm">Family vault nominee details</p>
          </div>
        </div>
        <span className="px-3 py-1 bg-purple-500/20 text-purple-400 font-bold rounded-full text-sm">
          {data.length} Clients
        </span>
      </div>

      {/* Client List */}
      <div className="space-y-3 max-h-[400px] overflow-y-auto pr-2">
        {data.map((client) => {
          const isExpanded = expandedClient === client.client_id;
          
          return (
            <div 
              key={client.client_id}
              className="bg-[#1A2942] rounded-lg border border-[#2D5A8F] overflow-hidden"
            >
              {/* Client Header */}
              <button
                onClick={() => setExpandedClient(isExpanded ? null : client.client_id)}
                className="w-full px-4 py-3 flex items-center justify-between hover:bg-[#1E3A5F]/50 transition-all"
              >
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 rounded-full bg-[#2D5A8F] flex items-center justify-center">
                    <FaUser className="text-gray-400" />
                  </div>
                  <div className="text-left">
                    <p className="text-white font-semibold">{client.client_name}</p>
                    <p className="text-gray-400 text-xs">{client.nominees.length} Nominee(s)</p>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <span className="text-gray-500 text-xs">
                    Updated: {formatDate(client.updated_at)}
                  </span>
                  {isExpanded ? (
                    <FaChevronUp className="text-gray-400" />
                  ) : (
                    <FaChevronDown className="text-gray-400" />
                  )}
                </div>
              </button>

              {/* Nominee Details */}
              {isExpanded && (
                <div className="px-4 pb-4 border-t border-[#2D5A8F]">
                  <p className="text-gray-500 text-xs uppercase tracking-wider my-3">Nominee Details</p>
                  <div className="space-y-3">
                    {client.nominees.map((nominee, idx) => (
                      <div 
                        key={idx}
                        className="bg-[#0F172A] rounded-lg p-4 border border-[#2D5A8F]"
                      >
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-[#FFD700] text-xs font-medium">
                            Nominee {idx + 1}
                          </span>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                          <div className="flex items-center space-x-2">
                            <FaUser className="text-gray-500" size={12} />
                            <div>
                              <p className="text-gray-500 text-xs">Name</p>
                              <p className="text-white text-sm font-medium">{nominee.name || '-'}</p>
                            </div>
                          </div>
                          <div className="flex items-center space-x-2">
                            <FaPhone className="text-gray-500" size={12} />
                            <div>
                              <p className="text-gray-500 text-xs">Contact</p>
                              <p className="text-white text-sm">{nominee.contact || '-'}</p>
                            </div>
                          </div>
                          <div className="flex items-center space-x-2">
                            <FaEnvelope className="text-gray-500" size={12} />
                            <div>
                              <p className="text-gray-500 text-xs">Email</p>
                              <p className="text-white text-sm truncate">{nominee.email || '-'}</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ClientNomineesView;
